import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { makeStyles } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InstructionComponentType } from '@scale/shared/defaults';
import { SelectedPathType } from '../types';
import { useProjectInstructionContext } from '../projectInstructionContext';
import SideNavigationItem from './sideNavigationItem';
import { useMount } from 'react-use-v13';
import { useRouter } from 'next/router';

const SideNavigation = ({
  handleSetSelectedPath,
}: {
  handleSetSelectedPath?: (_: SelectedPathType) => void;
}) => {
  const classes = useStyles();
  const {
    selectedPath,
    setSelectedPath: setSelectedPathFromContext,
    setSearchResult,
    components,
    isMobileView,
    isRapidPreview,
    disableScrollOnMount,
    doNotRenderChildrenSet,
  } = useProjectInstructionContext();
  const [openNav, setOpenNav] = useState<boolean>(true);
  const [openGroupComponents, setOpenGroupComponents] = useState<string[]>([]);
  const setSelectedPath = handleSetSelectedPath ?? setSelectedPathFromContext;

  const router = useRouter();
  const { query } = router;
  const { componentId, type } = query as Record<string, string>;

  useMount(() => {
    if (isMobileView || disableScrollOnMount) {
      return;
    }

    if (components.length) {
      // If a componentId and type are provided in the URL and are valid, don't set the first component
      if (componentId && type) {
        const componentExists = components.find(
          comp => comp.nodeId === componentId && comp.nodeType === type,
        );
        if (componentExists) {
          setSelectedPath([type as InstructionComponentType, componentId]);
          return;
        }
      }

      const firstComponent = components[0];
      const { nodeType, nodeId } = firstComponent;

      if (
        nodeType === InstructionComponentType.Group &&
        firstComponent.components &&
        !isRapidPreview
      ) {
        const firstChildNode = firstComponent.components[0];
        setOpenGroupComponents([...openGroupComponents, firstComponent.nodeId]);
        setSelectedPath([firstChildNode.nodeType, firstChildNode.nodeId]);
      } else {
        setSelectedPath([nodeType, nodeId]);
      }
    }
  });

  useEffect(() => {
    if (openNav) {
      const content = document.querySelector('#content') as HTMLDivElement,
        wrapper = document.querySelector('#wrapper') as HTMLDivElement,
        shadowTop = document.querySelector('#shadow-top') as HTMLDivElement,
        shadowBottom = document.querySelector('#shadow-bottom') as HTMLDivElement,
        contentScrollHeight = content.scrollHeight - wrapper.offsetHeight;

      const handleScroll = function () {
        // @ts-ignore
        const currentScroll = this.scrollTop / contentScrollHeight;
        shadowTop.style.opacity = String(Math.min(currentScroll, 0.4));
        shadowBottom.style.opacity = String(Math.min(1 - currentScroll, 0.4));
      };

      content.addEventListener('scroll', handleScroll);

      const hasVerticalScrollbar = content.scrollHeight > content.clientHeight;

      if (hasVerticalScrollbar) {
        shadowTop.style.opacity = '0';
        shadowBottom.style.opacity = '0.4';
      }
    }
  }, [openNav]);

  const handleNavItemClick = (component: any, isGroupChildSelected: boolean) => {
    const { nodeType, nodeId } = component;
    if (nodeType === InstructionComponentType.Group && component.components?.length) {
      if (openGroupComponents.includes(nodeId)) {
        const newOpenGroupComponents = openGroupComponents.filter(
          openGroupComponent => openGroupComponent !== nodeId,
        );
        setOpenGroupComponents(newOpenGroupComponents);
      } else {
        setOpenGroupComponents([...openGroupComponents, nodeId]);
      }
      if (!isRapidPreview && !isGroupChildSelected && !isMobileView) {
        const firstChildNode = component.components[0];
        setSelectedPath([firstChildNode.nodeType, firstChildNode.nodeId]);
      }
    } else {
      setSelectedPath([nodeType, nodeId]);
      // Shallow push the URL with the new componentId and type
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            componentId: nodeId,
            type: nodeType,
          },
        },
        undefined,
        { shallow: true },
      );
    }
    setSearchResult(null);
  };

  const findComponentById = (componentId: string): any | undefined => {
    return components.find((comp: any) => comp.content._id && comp?.content?._id === componentId);
  };

  return (
    <div className={classes.parentPositionSticky}>
      {openNav && (
        <div id="wrapper" className={isMobileView ? classes.smWrapper : classes.wrapper}>
          <div id="shadow-top" className={cx(classes.shadow, classes.shadowTop)}></div>
          <div id="shadow-bottom" className={cx(classes.shadow, classes.shadowBottom)}></div>
          <div id="content" className={isMobileView ? classes.smContainer : classes.container}>
            {components.map(component => {
              if (
                component?.content?._id &&
                doNotRenderChildrenSet &&
                doNotRenderChildrenSet.has(component.content._id)
              ) {
                return <></>;
              }
              const { nodeType, nodeId } = component;
              const [selectedType, selectedId] = selectedPath;
              const isGroupComponent = nodeType === InstructionComponentType.Group;
              const isGroupChildSelected =
                isGroupComponent &&
                _.some(
                  component.components,
                  c => c.nodeId === selectedId && selectedType === c.nodeType,
                );

              const isGroupOpen = openGroupComponents.includes(nodeId);

              return (
                <SideNavigationItem
                  key={nodeId}
                  nested={false}
                  component={component}
                  isGroupOpen={isGroupOpen}
                  onClickItem={(componentOverride?: any) =>
                    handleNavItemClick(componentOverride ?? component, isGroupChildSelected)
                  }
                  getComponentById={findComponentById}
                />
              );
            })}
          </div>
        </div>
      )}
      {!isMobileView && (
        <div className={classes.hideShowButton} onClick={() => setOpenNav(!openNav)}>
          <FontAwesomeIcon style={{ fontSize: 14 }} icon={openNav ? 'caret-left' : 'caret-right'} />
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  parentPositionSticky: {
    position: 'sticky',
    top: '10px',
    alignSelf: 'flex-start',
    display: 'flex',
    height: '100%',
  },
  container: {
    background: 'white',
    flex: 1,
    padding: 15,
    flexShrink: 0,
    minWidth: 250,
    maxWidth: 300,
    minHeight: '90%',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': { width: 0 },
    maxHeight: '75vh',
    color: theme.colors.gray75,
    '-webkit-user-select': 'none' /* Safari */,
    '-moz-user-select': 'none' /* Firefox */,
    '-ms-user-select': 'none' /* IE10+/Edge */,
    'user-select': 'none' /* Standard */,
  },
  smContainer: {
    background: 'white',
    flex: 1,
    flexShrink: 0,
    minHeight: '90%',
    maxHeight: '100%',
    overflowY: 'scroll',
    color: theme.colors.gray75,
    '-webkit-user-select': 'none' /* Safari */,
    '-moz-user-select': 'none' /* Firefox */,
    '-ms-user-select': 'none' /* IE10+/Edge */,
    'user-select': 'none' /* Standard */,
    paddingBottom: 100,
  },
  navLine: {
    padding: 10,
    '& > span': {
      paddingLeft: 10,
    },

    '&:hover': {
      cursor: 'pointer',
    },
  },
  mobileNavLine: {
    padding: 10,
    display: 'flex',
    background: theme.colors.gray01,
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `1px solid ${theme.colors.gray05}`,
    '& > span': {
      paddingLeft: 10,
      marginRight: 'auto',
    },

    '&:hover': {
      cursor: 'pointer',
    },
  },
  hideShowButton: {
    display: 'flex',
    justifyContent: 'center',
    background: 'white',
    border: `1px solid ${theme.colors.gray10}`,
    alignItems: 'center',
    width: 16,
    height: 40,
    textAlign: 'center',
    borderRadius: '0px 8px 7px 0px',
    padding: 4,
    marginBottom: 5,
    cursor: 'pointer',
  },

  wrapper: {
    border: `1px solid ${theme.colors.gray10}`,
    position: 'relative',
    borderRadius: '10px 0px 10px 10px',
    overflow: 'hidden',
  },
  smWrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  shadow: {
    position: 'absolute',
    left: 0,
    zIndex: 100,
    width: '100%',
    height: 10,
    background: 'red',
  },

  shadowTop: {
    top: 0,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.35), transparent)',
    opacity: 0,
  },

  shadowBottom: {
    bottom: 0,
    opacity: 0,
    background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.35), transparent)',
  },
}));

export default SideNavigation;
